import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { ButtonSwitch } from 'components/Button';
import { Card } from 'components/Card';
import { H2, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { getUserYearlyStatsRequest, User } from 'store/User';
import { centsToDollarsDisplay } from 'utils';

const Layout = styled(Card)`
  display: flex;
  padding: ${toRem(theme.space.md)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  align-self: stretch;
`;

const StyledSwitch = styled(ButtonSwitch)`
  padding: ${toRem(theme.space['2xs'])};

  button {
    padding: ${toRem(theme.space.xs)} ${toRem(theme.space.lg)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    padding: ${toRem(theme.space.xs)};

    button {
      padding: ${toRem(theme.space.sm)} ${toRem(theme.space.xl)};
    }
  }
`;

const Footer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  gap: ${toRem(theme.space.lg)};
  justify-content: center;

  p {
    color: ${theme.color.neutral[500]};
  }
`;

interface IEmployeeYearlyStatsProps {
  dataCy?: string;
  siteId?: string;
}

const currentYear = dayjs().year();
const previousYear = dayjs().subtract(1, 'year').year();

const EmployeeYearlyStats: React.FC<IEmployeeYearlyStatsProps> = ({ dataCy = 'employee-yearly-stats', siteId }) => {
  const dispatch = useDispatch();
  const payoutStatsByStatus = useSelector((state: IStore) => state.user?.yearlyStats?.byStatus || []);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_YEARLY_STATS_REQUEST));
  const [switchChecked, setSwitchChecked] = useState(false);

  const tips = payoutStatsByStatus?.reduce((acc, status) => acc + (status.net || 0), 0);
  const autograts = payoutStatsByStatus?.reduce((acc, status) => acc + (status.autoGratuity || 0), 0);

  useEffect(() => {
    dispatch(getUserYearlyStatsRequest(switchChecked ? previousYear : currentYear, siteId));
  }, [dispatch, switchChecked, siteId]);

  return (
    <Layout data-cy={dataCy}>
      <StyledSwitch data-cy={`${dataCy}-switch`} onChange={() => setSwitchChecked(!switchChecked)} checked={switchChecked} leftText={currentYear.toString()} rightText={previousYear.toString()} />
      <H2 fontWeight="bold" data-cy={`${dataCy}-total-amount`}>
        {isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(tips)}
      </H2>
      <Footer data-cy={`${dataCy}-footer`}>
        <Text size="xsmall" data-cy={`${dataCy}-tips`}>
          {isLoading ? <Skeleton width={100} /> : `Tips: ${centsToDollarsDisplay(tips - autograts)}`}
        </Text>
        <Text size="xsmall" data-cy={`${dataCy}-autograts`}>
          {isLoading ? <Skeleton width={100} /> : `Autograts: ${centsToDollarsDisplay(autograts)}`}
        </Text>
      </Footer>
    </Layout>
  );
};

export default EmployeeYearlyStats;
